import { useContext, useEffect, useState } from 'react';
import { castIriToId, getFileExtension } from '@europrocurement/l2d-utils';
import {
    ApiFichiersPostExpediteurEnum,
    ApiFichiersPostExpeditionEnum,
    FichiersDossierJsonldDocumentfileRead,
    FproDosDocFileJsonldDocumentfileRead,
} from '@europrocurement/l2d-domain/openApi/ApiFormalite';
import { DOC_TYPE_REQUIRED, DocTypeEnumType } from '@europrocurement/l2d-domain';
import { enqueueSnackbar } from 'notistack';
import useDossierDocuments, {
    DocumentExtension,
    DossierFichier,
    EXPEDITEUR_FORMALISTE,
    EXPEDITEUR_PRESCRIPTEUR,
    EXPEDITION_PRO,
} from './useDossierDocuments';
import { DisplayedDocument, DocumentPreview } from '../providers/types';
import DocumentContext from '../providers/DocumentContext';
import { useDocumentPreviewContext } from '../providers/DocumentPreviewContext';

type TypePromiseUpdateFichier = {
    success: boolean;
    files?: FichiersDossierJsonldDocumentfileRead[];
};
const useDossierDocumentsFiles = () => {
    const {
        documents,
        fichiers,
        setFichiers,
        getPrescriberDocuments,
        getPrescriberAdditionalDocuments,
        getFormalisteDocuments,
        getFormalisteAdditionalDocuments,
        replaceFichier,
        addFichier,
        removeFichier,
    } = useContext(DocumentContext);
    const { documentPreview, setDocumentPreview } = useDocumentPreviewContext();
    const {
        getFichierFromDocument,
        uploadDossierFichier,
        removeDossierFichier,
        updateDossierFichier,
        getFichierContent,
        uploadAdditionalDossierFichier,
    } = useDossierDocuments();
    const [prescriberItems, setPrescriberItems] = useState<Array<DisplayedDocument>>([]);
    const [formalisteItems, setFormalisteItems] = useState<Array<DisplayedDocument>>([]);
    const defaults = {
        fileName: null,
        feedback: null,
        showable: false,
        removable: false,
        editable: false,
        downloadable: false,
        showUploadBtn: false,
    };

    const formatDocumentsList = (documentsItems: Array<DisplayedDocument>) =>
        documentsItems
            .map((item) => {
                const result = { ...item };

                if (item.fichierId) {
                    result.uploadable = false;
                }

                return result;
            })
            .sort((a, b) => `${a.label}`.localeCompare(b.label));

    useEffect(() => {
        const prescriberDocs = formatDocumentsList([
            ...getPrescriberDocuments(defaults),
            ...getPrescriberAdditionalDocuments(defaults),
        ]);
        setPrescriberItems(prescriberDocs);

        const formalisteDocs = formatDocumentsList([
            ...getFormalisteDocuments(defaults),
            ...getFormalisteAdditionalDocuments(defaults),
        ]);
        setFormalisteItems(formalisteDocs);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [documents, fichiers]);

    const updateListItem = (query: Record<string, number>, payload: object) => {
        const buffer = prescriberItems.map((item) => {
            const match = Object.keys(query).every(
                (key: 'id' | 'fichierId') => item[key] === query[key],
            );

            if (match) {
                return { ...item, ...payload };
            }

            return { ...item };
        });

        setPrescriberItems(buffer);
    };

    const handleAddFile = async (
        dossierId: number,
        documentId: number,
        file: File,
        fichierId?: number,
    ) => {
        const document = documents.find((doc) => Number(doc.id) === documentId);

        if (!document) {
            throw new Error('No document found');
        }

        const query: Record<string, number> =
            fichierId !== undefined ? { fichierId } : { id: documentId };
        updateListItem(query, { uploading: true, uploadable: false });
        let existingFichier = null;

        if (fichierId) {
            existingFichier = fichiers.find((f: DossierFichier) => Number(f.id) === fichierId);
        }

        if (undefined === existingFichier) {
            existingFichier = getFichierFromDocument(document, fichiers);
        }

        if (existingFichier) {
            removeDossierFichier(existingFichier.id as number);
        }

        try {
            const fichier = await uploadDossierFichier(
                dossierId,
                `/formalite/types-documents/${document?.document?.id}` as string,
                file,
                document?.document?.type === DOC_TYPE_REQUIRED
                    ? EXPEDITEUR_PRESCRIPTEUR
                    : EXPEDITEUR_FORMALISTE,
                EXPEDITION_PRO,
            );

            if (existingFichier) {
                replaceFichier(existingFichier, fichier);
            } else {
                addFichier(fichier);
            }
        } catch (err) {
            console.error(err);
            updateListItem(query, { uploading: false });

            throw new Error('An error occurred during file transfer');
        }
    };

    const handleRemoveFile = async (fichierId: number) => {
        const fichier = fichiers.find((f: DossierFichier) => Number(f.id) === fichierId);

        if (!fichier) {
            throw new Error('No file found');
        }

        updateListItem({ fichierId }, { uploading: true });
        try {
            await removeDossierFichier(fichierId);
            removeFichier(fichier);
        } catch (error) {
            console.error(error);

            throw new Error('An error occurred during the file deletion');
        }
    };

    const getFilePreview = async (
        documentId: number,
        fichierId: number,
    ): Promise<DocumentPreview> => {
        const document = documents.find((doc) => Number(doc.id) === documentId);

        if (!document) {
            throw new Error('No document found');
        }

        const fichier = fichiers.find(
            (f: DossierFichier) =>
                Number(f.id) === fichierId && Number(f.typeDocDossier?.id) === documentId,
        );

        if (!fichier) {
            throw new Error('No file found');
        }

        const file = await getFichierContent(fichier.id as number);
        const dossierId = castIriToId(document?.dossier as string);
        const statusId = fichier?.statut?.id
            ? fichier?.statut?.id
            : castIriToId(document.statut as string);
        const fileName = fichier.fichier as string;
        const ext = getFileExtension(fileName);

        return {
            fileName,
            fileType: ext as DocumentExtension,
            dossierId,
            fichierId: fichier?.id as number,
            statusId,
            type: document?.document?.type as number,
            expediteurType: fichier?.expediteur as DocTypeEnumType,
            documentLabel: document.libelle as string,
            documentTypeId: document.id,
            content: file.data as unknown as Blob,
        };
    };

    const getDocumentTypes = (dossierId: number, docTypeId?: number, currentItemType?: number) =>
        documents
            .filter((document) => {
                const docDossierId = castIriToId(document?.dossier as string);
                // remove already filled document type by a file
                if (
                    fichiers.find(
                        (fichier) =>
                            fichier?.typeDocDossier?.id === document.id &&
                            fichier?.typeDocDossier?.id !== currentItemType,
                    )
                ) {
                    return false;
                }

                if (fichiers.find((fichier) => fichier?.typeDocDossier?.id === currentItemType)) {
                    return true;
                }

                // Filter available documents by documentType
                if (docTypeId) {
                    const documentType = document?.document?.type as number;
                    return docDossierId === dossierId && documentType === docTypeId;
                }

                return docDossierId === dossierId;
            })
            .map((document) => ({
                id: document?.id as number,
                label: document?.libelle as string,
            }))
            .sort((a, b) => `${a.label}`.localeCompare(b.label));

    const updateFichier = async (
        fichierId: number,
        payload: Record<string, unknown>,
    ): Promise<TypePromiseUpdateFichier> => {
        const res = await updateDossierFichier(fichierId, payload);
        if (res.status === 200) {
            let updatedFile: FproDosDocFileJsonldDocumentfileRead | undefined = res.data.files
                ? res.data.files[0]
                : undefined;
            if (res.data.files && res.data.files?.length > 1) {
                updatedFile = res.data.files.find((file) => file.id === fichierId);
            }
            if (updatedFile && documentPreview && documentPreview.fichierId === updatedFile.id) {
                setDocumentPreview({
                    ...documentPreview,
                    documentLabel: updatedFile.typeDocDossier?.libelle as string,
                    documentTypeId: updatedFile.typeDocDossier?.id,
                    statusId: updatedFile.statut?.id,
                    type: updatedFile.typeDocDossier?.document?.type as number,
                });
            }

            // Update fichiers state
            if (res.data.files && res.data.files.length > 1) {
                setFichiers((prevState) => {
                    const newState = [...prevState];
                    res.data.files!.forEach((file) => {
                        // find and update current file
                        const index = newState.findIndex((el) => el.id === file.id);
                        if (index !== -1) {
                            newState[index] = { ...newState[index], ...file };
                        } else {
                            // add new files if created from liasse
                            newState.push(file);
                        }
                    });
                    return newState;
                });
            }
            return { success: true, files: res.data.files };
        }
        enqueueSnackbar('Une erreur est survenue lors de la modification de fichier');
        return { success: false };
    };

    const getDocumentById = (documentId: number) => documents.find((doc) => doc.id === documentId);

    const handleAddFileOtherType = async (
        dossierId: number,
        file: File,
        expediteur: ApiFichiersPostExpediteurEnum,
        expedition: ApiFichiersPostExpeditionEnum,
        typageAuto?: boolean,
    ): Promise<FichiersDossierJsonldDocumentfileRead> => {
        try {
            const fichier = await uploadAdditionalDossierFichier(
                dossierId,
                file,
                expediteur,
                expedition,
                typageAuto || false,
            );
            addFichier(fichier);
            return fichier;
        } catch (err) {
            console.error(err);
            throw new Error('An error occurred during file transfer');
        }
    };

    return {
        prescriberItems,
        formalisteItems,
        getFilePreview,
        handleAddFile,
        handleRemoveFile,
        getDocumentTypes,
        updateFichier,
        getDocumentById,
        handleAddFileOtherType,
    };
};

export default useDossierDocumentsFiles;
